


















import {
	computed,
	defineComponent,
	PropType,
	Ref,
	watch,
} from '@vue/composition-api';
import deepEqual from 'deep-equal';
import {
	useIsError,
	useIsLoading,
	useIsUnstarted,
} from '@front-app/hooks/useAsyncStatus';
import { UserStore } from '@front-app/store/singletons/User';
import { PaginatedApi, usePaginatedApi } from '@front-app/hooks/usePaginatedApi';
import { usePaginationFromCount } from '@front-app/hooks/usePagination';
import { getGroupMemberPage } from '@front-app/services/GroupsApi/GroupsMemberApi';
import { GroupMember, GroupMemberWithEmail, UserRole } from '@front-app/services/UserApi/UserModel';
import { useGroupMembersStore } from '@front-app/store/singletons/GroupMembers';
import { MemberSort } from '../hooks/useSortMemberRadio';
import ListMembers from './ListMembers.vue';

export default defineComponent({
	components: { ListMembers },
	props: {
		groupId: {
			type: String,
			required: true,
		},
		roleFilter: {
			type: String as PropType<UserRole>,
			default: UserRole.MEMBER,
		},
		sortBy: {
			type: String as PropType<MemberSort>,
			default: MemberSort.ALPHABETICAL,
		},
		pageSize: {
			type: Number,
			required: true,
		},
	},
	setup(props) {
		const {
			getNumMembersByRole,
		} = useGroupMembersStore(computed(() => props.groupId));
		const numItems = computed(() => getNumMembersByRole([props.roleFilter]));
		const paginators = [
			UserRole.DEPARTEE,
			UserRole.INVITEE,
			UserRole.EXTERNAL_INVITEE,
			UserRole.MEMBER,
			UserRole.MODERATOR,
			UserRole.ADMINISTRATOR,
		].reduce((roleMap, role) => {
			const config = {
				renderedPageLength: props.pageSize,
				fetchedPageLength: 100,
				numItems,
			};
			const alphabeticalFetcher = (pageNum: number) => getGroupMemberPage(props.groupId, [role], pageNum, MemberSort.ALPHABETICAL);
			const lastJoinFetcher = (pageNum: number) => getGroupMemberPage(props.groupId, [role], pageNum, MemberSort.LAST_JOIN);
			const paginatedApiPerSort: Record<MemberSort, PaginatedApi<GroupMemberWithEmail | GroupMember>> = {
				[MemberSort.ALPHABETICAL]: usePaginatedApi(alphabeticalFetcher, config),
				[MemberSort.LAST_JOIN]: usePaginatedApi(lastJoinFetcher, config),
			};
			// eslint-disable-next-line no-param-reassign
			roleMap[role] = computed(() => paginatedApiPerSort[props.sortBy]);
			return roleMap;
		}, {} as Record<UserRole, Ref<PaginatedApi<GroupMemberWithEmail | GroupMember>>>);
		const currentPaginationData = computed(() => paginators[props.roleFilter].value);

		const {
			currentPage,
			numPages,
			hasNextPage,
			hasPrevPage,
			goToNextPage,
			goToPreviousPage,
			goToPage,
		} = usePaginationFromCount(numItems, props.pageSize, 1);
		const pagedMembers = computed(() => currentPaginationData.value.getPageAt(currentPage.value));

		const removeUser = (userId: string) => {
			const user = pagedMembers.value.find(({ id }) => id === userId);
			if (!user) {
				/* eslint-disable-next-line no-console */
				console.warn('Could not locate user to be deleted: ', userId);
				return;
			}
			currentPaginationData.value.removeItemAtPage(user, currentPage.value);
		};

		watch(computed((): [number, UserRole, MemberSort] => [currentPage.value, props.roleFilter, props.sortBy]), (curr, prev) => {
			if (prev && curr[1] !== prev[1] && curr[0] !== 1) {
				goToPage(1);
			} else if (!deepEqual(curr, prev)) {
				currentPaginationData.value.fetchPagesAround(curr[0]);
			}
		}, { immediate: true });

		watch(pagedMembers, (curr, prev) => {
			if (curr === prev) {
				return;
			}
			const ids = curr
				.filter(({ groupRole }) => groupRole !== UserRole.EXTERNAL_INVITEE)
				.map(({ id }) => id);
			UserStore.getInstance().fetchMany(ids);
		}, { immediate: true });

		const currentStatus = computed(() => currentPaginationData.value.getStatusAt(currentPage.value));
		const isLoadingMembers = useIsLoading(currentStatus);
		const hasNotFetchedMembers = useIsUnstarted(currentStatus);
		const hasErrorLoadingMembers = useIsError(currentStatus);

		return {
			isLoadingMembers,
			hasNotFetchedMembers,
			hasErrorLoadingMembers,
			pagedMembers,
			numPages,
			currentPage,
			hasNextPage,
			hasPrevPage,
			goToNextPage,
			goToPreviousPage,
			goToPage,
			removeUser,
		};
	},
});
